import dayjs from "dayjs";
import { downloadBlob } from "./DownloadUtils";
import {
  calculateDiscount,
  calculateItemTaxBreakdown,
  calculateTotalProduct,
  roundRupiah,
  WHOLE_RUPIAH,
} from "./AccountingUtil";
import {
  DEFAULT_BUYER_COUNTRY,
  DEFAULT_BUYER_DOCUMENT,
  DEFAULT_NON_NPWP_TIN,
  DEFAULT_TRX_CODE,
  DEFAULT_UNIT,
} from "../Constants/coretax";

const DEFAULT_TAX_INVOICE_OPT = "Normal";
const DEFAULT_GS_CODE = "000000";
const IDTKU_SUFFIX = "000000";

const escapeXml = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const formatXmlDate = (date) => {
  if (!date) return "";
  return dayjs(date).format("YYYY-MM-DD");
};

const mapGoodServiceOpt = (product) =>
  product?.type === "SERVICE" ? "B" : "A";

const optionalEl = (tag, value, indent = "      ") =>
  value
    ? `${indent}<${tag}>${escapeXml(value)}</${tag}>`
    : `${indent}<${tag}/>`;

// A faktur keluaran reports two taxes and no others: PPN in <VAT> and PPnBM in
// <STLG>. Withheld tax is income tax, not VAT, so a PPh 23 on the line has no
// place in the file at all — DJP would reject a faktur that declared one.
//
// A tax saved before `kind` existed is a PPN, because that is all the form
// could make back then.
const isVatEntry = (tax) => !tax.withholding && tax.kind !== "PPNBM";
const isLuxuryEntry = (tax) => !tax.withholding && tax.kind === "PPNBM";

// Coretax wants whole rupiah in every amount node, and PER-11/PJ/2025 wants
// the VAT charged on the ROUNDED DPP Nilai Lain — rounding a VAT worked out on
// the unrounded base is off by Rp1 often enough (Rp10.041: 1.105 against the
// correct 1.104). So every line is exported from a WHOLE_RUPIAH breakdown,
// including invoices posted to the sen before the rule: the file reports the
// faktur, and the faktur is whole rupiah either way.
const toFakturAmounts = (breakdown, options = {}) => {
  const entries = breakdown.taxes || [];
  const vatEntry = entries.find(isVatEntry) || null;
  const luxuryEntry = entries.find(isLuxuryEntry) || null;
  const { otherTaxBase } = breakdown;

  // A PPnBM on the line beats the export option, which predates per-line
  // luxury tax and stays as the fallback for invoices that do not carry one.
  const stlgRate = luxuryEntry
    ? (luxuryEntry.amount ?? 0)
    : options.stlgRate || 0;
  const stlg = luxuryEntry
    ? luxuryEntry.total
    : stlgRate
      ? roundRupiah((stlgRate / 100) * otherTaxBase)
      : 0;

  return {
    price: roundRupiah(breakdown.price),
    qty: breakdown.quantity,
    totalDiscount: roundRupiah(breakdown.itemDiscount),
    taxBase: breakdown.taxBase,
    otherTaxBase,
    vatRate: vatEntry?.amount ?? 0,
    vat: vatEntry?.total ?? 0,
    stlgRate,
    stlg,
  };
};

const buildTaxInvoiceNode = (transaction, options, documentSellerTin) => {
  const partner = transaction?.partner || {};
  const allItems = transaction?.items?.items || [];
  // One TIN for the whole document: the root <TIN> and every <SellerIDTKU>
  // have to identify the same seller or the file is internally inconsistent.
  const sellerTin = documentSellerTin;
  const buyerTin =
    options.buyerTin || partner.taxNumber || DEFAULT_NON_NPWP_TIN;
  const buyerName = options.buyerName || partner.name || "";
  const buyerAddress = options.buyerAddress || partner.address || "";
  const buyerEmail = options.buyerEmail || partner.email || "";
  const buyerIdtku = options.buyerIdtku || buyerTin + options.idtkuBranch;
  const sellerIdtku = options.sellerIdtku || sellerTin + options.idtkuBranch;
  const buyerDocumentNumber =
    options.buyerDocument === "TIN" && buyerTin
      ? "-"
      : options.buyerDocumentNumber;

  const discountType =
    transaction?.discount?.type === "PERCENTAGE" ? "percentage" : "fixed";
  const discountValue = transaction?.discount?.value;
  // The invoice decides whether the discount is taxed; the export must follow
  // it, otherwise the exported VAT contradicts the invoice total.
  const applyAfterTax = Boolean(transaction?.discount?.applyAfterTax);
  const subtotal = calculateTotalProduct(allItems);
  const totalDiscount = calculateDiscount(
    subtotal,
    discountType,
    discountValue,
  );

  const goodServiceNodes = allItems
    .map((item) => {
      const { product } = item;
      const b = calculateItemTaxBreakdown(
        item,
        subtotal,
        totalDiscount,
        applyAfterTax,
        null,
        WHOLE_RUPIAH,
      );

      const f = toFakturAmounts(b, options);

      return [
        "        <GoodService>",
        `          <Opt>${mapGoodServiceOpt(product)}</Opt>`,
        `          <Code>${escapeXml(options.goodServiceCode)}</Code>`,
        // DJP rejects an empty <Name>, and a product the closed period guard
        // hides is not something the user can fix from here — fall back to the
        // line's own note rather than hand them a file that fails at upload.
        optionalEl(
          "Name",
          product?.name || item.description || "-",
          "          ",
        ),
        `          <Unit>${escapeXml(options.unit)}</Unit>`,
        `          <Price>${f.price}</Price>`,
        `          <Qty>${f.qty}</Qty>`,
        `          <TotalDiscount>${f.totalDiscount}</TotalDiscount>`,
        `          <TaxBase>${f.taxBase}</TaxBase>`,
        `          <OtherTaxBase>${f.otherTaxBase}</OtherTaxBase>`,
        `          <VATRate>${f.vatRate}</VATRate>`,
        `          <VAT>${f.vat}</VAT>`,
        `          <STLGRate>${f.stlgRate}</STLGRate>`,
        `          <STLG>${f.stlg}</STLG>`,
        "        </GoodService>",
      ].join("\n");
    })
    .join("\n");

  return [
    "    <TaxInvoice>",
    `      <TaxInvoiceDate>${formatXmlDate(options.taxInvoiceDate || transaction?.createdAt)}</TaxInvoiceDate>`,
    `      <TaxInvoiceOpt>${escapeXml(options.taxInvoiceOpt)}</TaxInvoiceOpt>`,
    `      <TrxCode>${escapeXml(options.trxCode)}</TrxCode>`,
    optionalEl("AddInfo", options.addInfo),
    optionalEl("CustomDoc", options.customDoc),
    optionalEl("CustomDocMonthYear", options.customDocMonthYear),
    optionalEl("RefDesc", options.refDesc || transaction?.referenceNumber),
    optionalEl("FacilityStamp", options.facilityStamp),
    `      <SellerIDTKU>${escapeXml(sellerIdtku)}</SellerIDTKU>`,
    `      <BuyerTin>${escapeXml(buyerTin)}</BuyerTin>`,
    `      <BuyerDocument>${escapeXml(options.buyerDocument)}</BuyerDocument>`,
    `      <BuyerCountry>${escapeXml(options.buyerCountry)}</BuyerCountry>`,
    optionalEl("BuyerDocumentNumber", buyerDocumentNumber),
    optionalEl("BuyerName", buyerName),
    optionalEl("BuyerAdress", buyerAddress),
    optionalEl("BuyerEmail", buyerEmail),
    `      <BuyerIDTKU>${escapeXml(buyerIdtku)}</BuyerIDTKU>`,
    "      <ListOfGoodService>",
    goodServiceNodes,
    "      </ListOfGoodService>",
    "    </TaxInvoice>",
  ].join("\n");
};

const withDefaults = (options = {}) => ({
  trxCode: options.trxCode || DEFAULT_TRX_CODE,
  taxInvoiceOpt: options.taxInvoiceOpt || DEFAULT_TAX_INVOICE_OPT,
  taxInvoiceDate: options.taxInvoiceDate || "",
  refDesc: options.refDesc || "",
  sellerIdtku: options.sellerIdtku || "",
  unit: options.unit || DEFAULT_UNIT,
  goodServiceCode: options.goodServiceCode || DEFAULT_GS_CODE,
  idtkuBranch: options.idtkuBranch || IDTKU_SUFFIX,
  addInfo: options.addInfo || "",
  customDoc: options.customDoc || "",
  customDocMonthYear: options.customDocMonthYear || "",
  facilityStamp: options.facilityStamp || "",
  sellerTin: options.sellerTin || "",
  buyerTin: options.buyerTin || "",
  buyerName: options.buyerName || "",
  buyerAddress: options.buyerAddress || "",
  buyerEmail: options.buyerEmail || "",
  buyerDocument: options.buyerDocument || DEFAULT_BUYER_DOCUMENT,
  buyerDocumentNumber: options.buyerDocumentNumber || "",
  buyerIdtku: options.buyerIdtku || "",
  buyerCountry: options.buyerCountry || DEFAULT_BUYER_COUNTRY,
  stlgRate: Number(options.stlgRate) || 0,
});

// A TaxInvoiceBulk document carries a single root <TIN>, so every invoice in
// it must belong to the same seller. Mixing sellers would silently file one
// company's invoices under another company's tax number.
const resolveDocumentSellerTin = (list, options) => {
  if (options.sellerTin) {
    return options.sellerTin;
  }

  const tins = [
    ...new Set(
      list.map((tx) => tx?.business?.taxNumber || "").filter(Boolean),
    ),
  ];

  if (tins.length > 1) {
    throw new Error(
      `Cannot export one Faktur Keluaran file for multiple sellers (${tins.join(", ")}). Export each seller separately.`,
    );
  }

  return tins[0] || "";
};

export const generateFakturKeluaranXml = (transactionOrList, options) => {
  const list = Array.isArray(transactionOrList)
    ? transactionOrList
    : [transactionOrList].filter(Boolean);
  const opts = withDefaults(options);

  const sellerTin = resolveDocumentSellerTin(list, opts);

  const taxInvoiceNodes = list
    .map((tx) => buildTaxInvoiceNode(tx, opts, sellerTin))
    .filter(Boolean)
    .join("\n");

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<TaxInvoiceBulk xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="TaxInvoice.xsd">',
    `  <TIN>${escapeXml(sellerTin)}</TIN>`,
    "  <ListOfTaxInvoice>",
    taxInvoiceNodes,
    "  </ListOfTaxInvoice>",
    "</TaxInvoiceBulk>",
  ].join("\n");
};

export const downloadFakturKeluaranXml = (
  transactionOrList,
  options,
  fileName,
) => {
  const isBulk = Array.isArray(transactionOrList);
  const xml = generateFakturKeluaranXml(transactionOrList, options);
  const blob = new Blob([xml], { type: "application/xml" });

  let finalName = fileName;
  if (!finalName) {
    if (isBulk) {
      finalName = `FakturKeluaran-Bulk-pajakInd-${dayjs().format("YYYYMMDD-HHmmss")}.xml`;
    } else {
      const tx = transactionOrList || {};
      finalName = `FakturKeluaran-pajakInd-${tx.invoiceNumber || tx.id || "export"}.xml`;
    }
  }

  downloadBlob(blob, finalName);
};
